import React from 'react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import useBundleTranslation from 'i18n';
import { useSelector } from 'react-redux';
import { IDomain } from 'store/conciergeSlice';
import IconHandler from 'components/common/icon/IconHandler';

interface IProceedButtonProps {
    onRemoveAuthMessages?: () => void;
    authRequiredToolNames?: string[];
}

export const ProceedButton: React.FC<IProceedButtonProps> = ({ onRemoveAuthMessages, authRequiredToolNames = [] }) => {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];
    const selectedDomains = useSelector((state: any) => state.concierge.selectedDomains);
    const authorizedTools = useSelector((state: any) => state.concierge.authorizedTools) as string[];

    const activeAuthRequiredToolNames = React.useMemo(() => {
        return authRequiredToolNames.filter((toolName) => !authorizedTools.includes(toolName));
    }, [authRequiredToolNames, authorizedTools]);

    const filteredDomains = React.useMemo(() => {
        return availableDomains.filter((domain) => {
            const hasAuthRequiredService = domain.customServices?.some((service) => {
                return activeAuthRequiredToolNames.includes(service.name);
            });

            if (hasAuthRequiredService) {
                return false;
            }

            if (selectedDomains && selectedDomains.length > 0) {
                return selectedDomains.includes(domain.name);
            }

            return true;
        });
    }, [availableDomains, activeAuthRequiredToolNames, selectedDomains]);

    const handleProceed = () => {
        if (onRemoveAuthMessages) {
            onRemoveAuthMessages();
        }
    };

    if (filteredDomains.length === 0) {
        return null;
    }

    return (
        <Stack spacing={1}>
            <Typography variant="body2" sx={{ mb: 1 }}>
                {t('proceed_button.text')}{' '}
                <Tooltip
                    title={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {filteredDomains.map((domain, idx) => {
                                const iconProps = {
                                    type: domain.icon_ind,
                                    value:
                                        domain.icon_ind === 'upload'
                                            ? `data:image/png;base64,${domain.icon_data}`
                                            : domain.icon_name,
                                };

                                return (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                            {domain.icon_ind !== 'none' && (
                                                <IconHandler icon={iconProps} sx={{ fontSize: '16px' }} />
                                            )}
                                        </Box>
                                        <span>{domain.name}</span>
                                    </Box>
                                );
                            })}
                        </Box>
                    }
                    arrow
                >
                    <Box
                        component="span"
                        sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                        }}
                    >
                        {t('proceed_button.domains')}
                    </Box>
                </Tooltip>
                .
            </Typography>
            <Button variant="outlined" onClick={handleProceed} sx={{ width: 'fit-content', height: '28px' }}>
                {t('proceed_button.button')}
            </Button>
        </Stack>
    );
};
